import React from 'react';
import type { GetServerSidePropsResult } from 'next';

import type { Filters, HotelResults } from '@/interfaces';
import { runAllMiddlewares } from '@/server/middleware';
import { fetchHotel, fetchOffersForHotel } from '@/api/fetcher';
import { browserQueryParamsToFilters, filtersToApiQueryParams } from '@/lib/utils/queryParamFilterConversions';

import { wrapper } from '@/state/store';
import { actions } from '@/state/filterActions';
import { actions as hotelResultActions } from '@/state/hotelResultActions';
import { actions as hotelActions } from '@/state/hotelActions';

import HotelLayout from '@/components/hotel/HotelLayout';
import HotelDescription from '@/components/hotel/HotelDescription';
import HotelResultList from '@/components/hotel/HotelResultList';
import HotelFilterPreview from '@/components/hotel/HotelFilterPreview';
import HotelOriginDropdown from '@/components/filters/origin/HotelOriginDropdown';
import HotelTravelTimePicker from '@/components/filters/travelTime/HotelTravelTimePicker';
import { EditPanel } from '@/components/filters/EditPanel';
import HotelPagination from '@/components/hotel/HotelPagination';
import { getPageCounter } from 'shared/prometheus/prometheus';

const getPreferredSupplier = (hotelResults: HotelResults): string | undefined =>
    hotelResults.offers.length > 0 ? hotelResults.offers[0].supplier : undefined;

const HotelPage = (props) => {
    wrapper.useHydration(props);

    return (
        <HotelLayout>
            <HotelDescription />
            <h2 className="u-screen-reader-only">Alternativer for avreise og hjemreise</h2>
            <EditPanel preview={<HotelFilterPreview />}>
                <div className="edit-panel-hotelfilters finn-reise--pakkereiser-hotell" data-testid="hotelFilter">
                    <div className="align-bottom u-r-size1of3 u-pr8--gt768">
                        <HotelOriginDropdown />
                    </div>
                    <div className="align-bottom u-r-size2of3">
                        <HotelTravelTimePicker />
                    </div>
                </div>
            </EditPanel>
            <HotelResultList />
            <HotelPagination />
        </HotelLayout>
    );
};

export const getServerSideProps = wrapper.getServerSideProps(
    (store) =>
        // biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
        async ({ req, res, query }): Promise<GetServerSidePropsResult<any>> => {
            const props = await runAllMiddlewares(req, res, query);

            const filters: Partial<Filters> = browserQueryParamsToFilters(query);
            store.dispatch(actions.filter(filters));

            const apiQueryParams = filtersToApiQueryParams(filters);
            const { statusCode: offerStatusCode, data: offerData } = await fetchOffersForHotel(apiQueryParams);
            const preferredSupplier = offerStatusCode === 200 ? getPreferredSupplier(offerData as HotelResults) : undefined;
            const { statusCode: hotelStatusCode, data: hotelData } = await fetchHotel({ ...apiQueryParams, preferredSupplier });

            if (hotelStatusCode === 200) {
                store.dispatch(hotelActions.setHotel(hotelData));
            }

            if (offerStatusCode === 200) {
                store.dispatch(hotelResultActions.setResults(offerData));
            } else {
                store.dispatch(hotelResultActions.setError());
            }
            getPageCounter().inc({ app: 'pp-web', page: 'hotell' });
            return { props: { ...props } };
        }
);

export default HotelPage;
